import { BaseAction } from "../../../core/actions/BaseAction";
import { addAncillaryToOffer, getAdditionalOffers } from "../../../app/api/getOffers";
import { getBooking } from "../../../app/api/getBooking";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import {
  AncillaryStatusEnum,
  AdmissionStatusEnum,
} from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBookedOffer, IBookingResponse, ITrip } from "../../../Orchestrator/Types/Index";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";

export interface IPreSelectAddonsForExchangeParams {
  exchangeOperationId: string;
  selectedPassengerForCancel: string[];
  selectedPassengerForCancelInBound: string[];
  // Optional: pass the freshly-fetched booking directly so the action
  // doesn't rely on `bookingResponseAtom`, which can be stale or contain
  // offers from a previous exchange session. When supplied, we skip the
  // atom read entirely. PreExchangeOperationStartAction passes this.
  bookingOverride?: IBookingResponse;
}

export interface IPreSelectCandidate {
  ancillaryId: string;
  passengerId: string;
  passengerExternalRef: string;
  productCode: string;
  productId: string;
  direction: string;
  oldTripId: string;
  oldLegId: string;
}

export interface IPreSelectMatch {
  bookedOfferId: string;
  additionalOfferId: string;
  ancillaryOfferId: string;
  passengerExternalRef: string;
  coveredTripId: string;
}

const SESSION_KEY = "cdtAddonsPreSelected";

function safeSessionGet(key: string): string | null {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem(key);
}

function safeSessionSet(key: string, value: string): void {
  if (typeof window !== "undefined") sessionStorage.setItem(key, value);
}

export class PreSelectAddonsForExchangeAction extends BaseAction<
  IDefaultActionResult,
  IPreSelectAddonsForExchangeParams
> {
  protected override deduplicate = true;

  // Exposed for tests.
  public candidates: IPreSelectCandidate[] = [];
  public matches: IPreSelectMatch[] = [];

  private enumerateCandidates(
    booking: IBookingResponse["booking"],
  ): IPreSelectCandidate[] {
    const candidates: IPreSelectCandidate[] = [];
    const trips: ITrip[] = booking?.trips || [];
    const passengers = booking?.passengers || [];

    // Exchange offers (the targets we'll POST to) are excluded here — they carry
    // a PREBOOKED admission. Old (source) offers are everything else that isn't
    // REFUNDED. A passenger not participating in the change-date simply won't
    // have a matching exchange offer, so matchCandidates() naturally drops them.
    const oldOffers = (booking?.bookedOffers || []).filter((o: IBookedOffer) =>
      (o.admissions || []).every((a) => a?.status !== AdmissionStatusEnum.REFUNDED) &&
      !(o.admissions || []).some((a) => a?.status === AdmissionStatusEnum.PREBOOKED),
    );

    for (const offer of oldOffers) {
      const trip = trips.find((t) => t.id === offer.tripCoverage?.coveredTripId);
      if (!trip) {
        console.warn(`[PreSelectAddonsForExchangeAction] no trip found for offer ${offer.offerId} (coveredTripId=${offer.tripCoverage?.coveredTripId})`);
        continue;
      }

      for (const anc of offer.ancillaries || []) {
        if (anc.status === AncillaryStatusEnum.REFUNDED) continue;
        const passengerId = (anc.passengerIds || [])[0];
        if (!passengerId) continue;
        const passenger = passengers.find((p: any) => p.id === passengerId);
        if (!passenger?.externalRef) continue;
        const productId = (anc.products || [])[0]?.productId;
        if (!productId) continue;
        const product = (offer.products || []).find((p: any) => p.id === productId);
        const productCode = product?.code || product?.id || productId;

        candidates.push({
          ancillaryId: anc.id,
          passengerId,
          passengerExternalRef: passenger.externalRef,
          productCode,
          productId,
          direction: trip.direction || "",
          oldTripId: trip.id,
          oldLegId: (anc.tripCoverage?.coveredLegIds || [])[0] || "",
        });
      }
    }
    return candidates;
  }

  private async matchCandidates(
    bookingId: string,
    booking: IBookingResponse["booking"],
    candidates: IPreSelectCandidate[],
  ): Promise<IPreSelectMatch[]> {
    const matches: IPreSelectMatch[] = [];
    const trips: ITrip[] = booking?.trips || [];

    const exchangeOffers = (booking?.bookedOffers || []).filter((o: IBookedOffer) =>
      (o.admissions || []).some((a: any) => a?.status === AdmissionStatusEnum.PREBOOKED),
    );

    // Cache additional offers per exchange offer id to avoid duplicate fetches.
    const additionalByBookedOfferId: Record<string, any[]> = {};
    for (const exch of exchangeOffers) {
      try {
        const res = await getAdditionalOffers(bookingId, exch.offerId);
        if (res.status === "ok") {
          additionalByBookedOfferId[exch.offerId] = res.answer?.offers || [];
        } else {
          additionalByBookedOfferId[exch.offerId] = [];
        }
      } catch (err) {
        console.warn(`[PreSelectAddonsForExchangeAction] getAdditionalOffers failed for ${exch.offerId}:`, err);
        additionalByBookedOfferId[exch.offerId] = [];
      }
    }

    for (const candidate of candidates) {
      let matched: IPreSelectMatch | null = null;
      for (const exch of exchangeOffers) {
        const exchTrip = trips.find((t) => t.id === exch.tripCoverage?.coveredTripId);
        if ((exchTrip?.direction || "") !== candidate.direction) continue;
        const passengerCovered = (exch.admissions || []).some((a: any) =>
          (a?.passengerIds || []).includes(candidate.passengerId),
        );
        if (!passengerCovered) continue;

        const additionalOffers = additionalByBookedOfferId[exch.offerId] || [];
        for (const additional of additionalOffers) {
          const part = (additional?.ancillaryOfferParts || []).find((p: any) => {
            const partProductId = (p?.products || [])[0]?.productId;
            const product = (additional?.products || []).find((pr: any) => pr.id === partProductId);
            const code = product?.code || product?.id || partProductId;
            return code === candidate.productCode;
          });
          if (part) {
            matched = {
              bookedOfferId: exch.offerId,
              additionalOfferId: additional.offerId,
              ancillaryOfferId: part.id,
              passengerExternalRef: candidate.passengerExternalRef,
              coveredTripId: exch.tripCoverage?.coveredTripId || "",
            };
            break;
          }
        }
        if (matched) break;
      }

      if (matched) {
        matches.push(matched);
      } else {
        console.warn(
          `[PreSelectAddonsForExchangeAction] No match for productCode=${candidate.productCode} passenger=${candidate.passengerExternalRef} direction=${candidate.direction}`,
        );
      }
    }
    return matches;
  }

  private async postMatches(
    bookingId: string,
    matches: IPreSelectMatch[],
  ): Promise<{ attempted: number; succeeded: number }> {
    let succeeded = 0;
    for (const match of matches) {
      try {
        const res = await addAncillaryToOffer(
          {
            offerId: match.additionalOfferId,
            ancillaryOfferId: match.ancillaryOfferId,
            passengerRefs: [match.passengerExternalRef],
            tripCoverage: { coveredTripId: match.coveredTripId },
          },
          bookingId,
          match.bookedOfferId,
          false,
        );
        if (res.status === "ok") {
          succeeded += 1;
        } else {
          console.warn(
            `[PreSelectAddonsForExchangeAction] POST returned non-ok for ${match.bookedOfferId}/${match.ancillaryOfferId}:`,
            (res as any)?.message,
          );
        }
      } catch (err) {
        console.warn(
          `[PreSelectAddonsForExchangeAction] POST failed for ${match.bookedOfferId}/${match.ancillaryOfferId}:`,
          err,
        );
      }
    }
    return { attempted: matches.length, succeeded };
  }

  override async run(): Promise<IDefaultActionResult> {
    const result: IDefaultActionResult = { data: null, message: "", status: "fail" };
    try {
      const exchangeOperationId = this.params?.exchangeOperationId;
      if (!exchangeOperationId) {
        result.message = "Missing exchangeOperationId";
        return result;
      }

      if (safeSessionGet(SESSION_KEY) === exchangeOperationId) {
        result.status = "success";
        return result;
      }

      // Prefer the caller-supplied booking (passed by PreExchangeOperationStartAction
      // / StartPreExchangeOperationAction immediately after their re-fetch) so we
      // can't be confused by a stale atom containing offers from a previous test
      // session. Fall back to the atom only if no override is provided.
      const overrideBooking = this.params?.bookingOverride?.booking;
      const atomBooking = overrideBooking
        ?? getRecoilStateValue<IBookingResponse>("bookingResponseAtom")?.booking;
      if (!atomBooking?.id) {
        result.status = "success";
        return result;
      }

      const hasPrebookedExchangeOffer = (b: IBookingResponse["booking"]): boolean =>
        (b?.bookedOffers || []).some((o: IBookedOffer) =>
          (o.admissions || []).some((a) => a?.status === AdmissionStatusEnum.PREBOOKED),
        );

      // The booking in the atom (or override) can be the snapshot taken right
      // after postPreExchangeOperation — before the new exchange offer
      // (PREBOOKED) has actually been attached. If so, re-fetch until we can
      // see it.
      let booking = atomBooking;
      if (!hasPrebookedExchangeOffer(booking)) {
        try {
          const fresh = await getBooking(atomBooking.id, false);
          if (fresh.status === "ok" && fresh.answer?.booking) {
            booking = fresh.answer.booking;
            updateRecoilState("bookingResponseAtom", fresh.answer);
          } else {
            console.warn("[PreSelectAddonsForExchangeAction] re-fetch returned non-ok, using atom booking:", (fresh as any)?.status);
          }
        } catch (err) {
          console.warn("[PreSelectAddonsForExchangeAction] re-fetch failed, using atom booking:", err);
        }
      }

      this.candidates = this.enumerateCandidates(booking);
      this.matches = await this.matchCandidates(booking.id, booking, this.candidates);

      const postOutcome = await this.postMatches(booking.id, this.matches);

      if (postOutcome.attempted > 0 && postOutcome.succeeded === 0) {
        // Every POST failed — return fail (telemetry). Do NOT set the sessionStorage
        // guard so a retry on the next CDT entry is allowed.
        result.message = "All POST attempts failed";
        return result;
      }

      if (postOutcome.succeeded > 0) {
        try {
          const refreshed = await getBooking(booking.id, false);
          if (refreshed.status === "ok" && refreshed.answer) {
            updateRecoilState("bookingResponseAtom", refreshed.answer);
          } else {
            console.warn("[PreSelectAddonsForExchangeAction] booking refresh returned non-ok:", (refreshed as any)?.status);
          }
        } catch (err) {
          console.warn("[PreSelectAddonsForExchangeAction] booking refresh failed (non-fatal):", err);
        }
      }

      // Integration wiring lives in StartPreExchangeOperationAction; the modal
      // pre-check is seeded by AddOnsInitStep when isChangeDateStateAtom is set.
      safeSessionSet(SESSION_KEY, exchangeOperationId);
      result.status = "success";
    } catch (error: any) {
      console.error("[PreSelectAddonsForExchangeAction] error:", error);
      result.message = error?.message || "";
    }
    return result;
  }
}

export const preSelectAddonsForExchangeAction = new PreSelectAddonsForExchangeAction();
